import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import type { ProcessingMode } from '@/contexts/CaptureContext'
import type { PromoPolicy } from '@/lib/promoPolicy'
import { useAuth } from '@/hooks/useAuth'
import { useMemories } from '@/hooks/useMemories'
import { useChildren } from '@/hooks/useChildren'
import { useEntitlements } from '@/hooks/useEntitlements'
import { useFeedEngine } from '@/hooks/useFeedEngine'
import { useMemoryProcessingStatus } from '@/hooks/useMemoryProcessingStatus'
import { useDeferredIntent, type IntentKind } from '@/contexts/DeferredIntentContext'
import { useToast } from '@/hooks/use-toast'
import BannerAdSlot from '@/components/ads/BannerAdSlot'
import StreakCard from '@/components/cards/StreakCard'
import FeedMemoryCard from '@/components/cards/FeedMemoryCard'
import GameCard from '@/components/cards/GameCard'
import TipCard from '@/components/cards/TipCard'
import SparkCard from '@/components/cards/SparkCard'
import MilestoneCard from '@/components/cards/MilestoneCard'
import CollectionCard from '@/components/cards/CollectionCard'
import MoriMessageCard from '@/components/cards/MoriMessageCard'
import type { FeedCard } from '@/components/cards/types'
import type { MemoryWithChildren } from '@/hooks/useMemories'
import type { RecordingOpts } from '@/components/HomeFeed'

// ── Types ──────────────────────────────────────────────────────────────────────

interface WaitScreenProps {
  processingMode: ProcessingMode
  promoPolicy: PromoPolicy | null
  /** The async save pipeline. Always called with null (adToken param is unused). */
  processor: (adToken: string | null) => Promise<string>
  /**
   * Fires exactly once after the memory's AI processing actually finishes
   * (or immediately for `skip` mode). Hosts the streak / acorn / event
   * fanout that used to run inline in the processor — kept here so the
   * "+N acorns" toast doesn't appear before processing is complete.
   */
  onProcessingFinalized?: (memoryId: string) => Promise<void> | void
  onComplete: (memoryId: string) => void
  onClose: () => void
}

type DisplayItem =
  | { kind: 'card'; card: FeedCard; key: string }
  | { kind: 'ad'; key: string }

// Matches HomeFeed's CARD_GAP so swipe stride is identical.
const CARD_GAP = 12

// ── CTA → IntentKind mapping (only actionable CTA types that produce an intent) ──
const CTA_TO_INTENT: Partial<Record<string, IntentKind>> = {
  capture_memory: 'capture_memory',
  open_upgrade:   'open_upgrade',
  open_settings:  'open_settings',
  open_mori_chat: 'open_mori_chat',
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function WaitScreen({
  processingMode,
  promoPolicy: _promoPolicy, // kept for API compat — tier gate now uses useEntitlements
  processor,
  onProcessingFinalized,
  onComplete,
  onClose,
}: WaitScreenProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { user } = useAuth()
  const { showAds, isLoading: entitlementsLoading } = useEntitlements()
  const { setIntent, isCurrent } = useDeferredIntent()

  // ── Processor state ──────────────────────────────────────────────────────
  const [memoryId, setMemoryId] = useState<string | null>(null)
  const [processorError, setProcessorError] = useState<string | null>(null)
  const calledRef = useRef(false)

  useEffect(() => {
    if (calledRef.current) return
    calledRef.current = true
    processor(null)
      .then((id) => setMemoryId(id))
      .catch((err) => {
        console.error('[WaitScreen] processor failed:', err)
        Sentry.captureException(err, { tags: { flow: 'wait-screen' } })
        setProcessorError((err as Error).message || '')
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Processing status ────────────────────────────────────────────────────
  const { isProcessingComplete, isTranscriptionComplete } = useMemoryProcessingStatus(memoryId)

  const isComplete =
    processingMode === 'skip'
      ? true
      : processingMode === 'full'
        ? isProcessingComplete
        : isTranscriptionComplete // 'transcript_only'

  // Fire the post-processing fanout (streak / acorns / events) once when
  // AI processing actually finishes. Splitting this from the processor
  // avoids the "+N acorns" toast appearing before the memory is processed.
  const fanoutFiredRef = useRef(false)
  useEffect(() => {
    if (fanoutFiredRef.current) return
    if (!isComplete || memoryId === null) return
    fanoutFiredRef.current = true
    void onProcessingFinalized?.(memoryId)
  }, [isComplete, memoryId, onProcessingFinalized])

  // ── Min-wait timer ──────────────────────────────────────────────────────
  // 20 s ticking countdown only for: free tier WITH actual AI work to wait on.
  // 5 s silent floor for everyone else — paid (any mode), and free + skip
  // (user bailed the ad, no AI is running, so a 20 s wait would be punitive).
  const timerStartedRef = useRef(false)
  const [minWaitElapsed, setMinWaitElapsed] = useState(false)
  const [countdown, setCountdown] = useState(20)

  useEffect(() => {
    if (entitlementsLoading) return
    if (timerStartedRef.current) return
    timerStartedRef.current = true

    const useShortFloor = !showAds || processingMode === 'skip'
    if (useShortFloor) {
      const id = setTimeout(() => setMinWaitElapsed(true), 5_000)
      return () => clearTimeout(id)
    }

    const end = Date.now() + 20_000
    const id = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((end - Date.now()) / 1000))
      setCountdown(remaining)
      if (remaining === 0) {
        clearInterval(id)
        setMinWaitElapsed(true)
      }
    }, 200) // 5× per second for smooth countdown
    return () => clearInterval(id)
  }, [entitlementsLoading, showAds, processingMode])

  // ── Safety-net timeout — if AI processing stalls (edge fn returned
  // memoryId but never updated ai_status), let the user out after 3 minutes.
  const [processingTimedOut, setProcessingTimedOut] = useState(false)
  useEffect(() => {
    if (!memoryId || processingTimedOut) return
    const id = setTimeout(() => setProcessingTimedOut(true), 3 * 60_000)
    return () => clearTimeout(id)
  }, [memoryId, processingTimedOut])

  // ── canExit ──────────────────────────────────────────────────────────────
  const canExit = (isComplete || processingTimedOut) && minWaitElapsed && memoryId !== null

  // ── Skip-mode "Saved!" toast — fires for any tier in skip mode once the
  // memory id lands. There's no AI work, so the toast is the user's signal
  // that the save itself succeeded.
  const skipToastFiredRef = useRef(false)
  useEffect(() => {
    if (!skipToastFiredRef.current && processingMode === 'skip' && memoryId !== null) {
      skipToastFiredRef.current = true
      toast({ description: t('capture.wait.savedToast') })
    }
  }, [processingMode, memoryId, toast, t])

  // ── Feed engine — mirrored exactly from HomeFeed ─────────────────────────
  const sessionId = useRef(crypto.randomUUID()).current
  const accountCreatedAt = user?.created_at ?? new Date().toISOString()
  const { memories, isLoading: memoriesLoading } = useMemories()
  const { children, isLoading: childrenLoading } = useChildren()

  const {
    cards: engineCards,
    trackCardInteraction,
    dismissCard,
    markPinnedRead,
    dismissPinnedInstance,
  } = useFeedEngine(
    // TODO(wait-feed): replace with a wait-surface-specific card selector once we
    // have data on which cards perform well in this context. For MVP we mirror
    // HomeFeed exactly so cards behave identically across surfaces.
    sessionId, accountCreatedAt, memories, memoriesLoading, children, childrenLoading,
  )

  // ── Build display items — interleave BannerAdSlot ────────────────────────
  // Paid tier (showAds === false) → content cards only.
  // Free tier → banner at slot indices 3, 7, 11, … (after every 3 content cards).
  const displayItems = useMemo<DisplayItem[]>(() => {
    if (!showAds) {
      return engineCards.map((card, i) => ({ kind: 'card', card, key: `card-${i}` }))
    }
    const items: DisplayItem[] = []
    let cardIdx = 0
    let p = 0
    while (cardIdx < engineCards.length) {
      if (p >= 3 && p % 4 === 3) {
        items.push({ kind: 'ad', key: `ad-${p}` })
      } else {
        items.push({ kind: 'card', card: engineCards[cardIdx], key: `card-${cardIdx}` })
        cardIdx++
      }
      p++
    }
    return items
  }, [showAds, engineCards])

  // Dev diagnostic — lets us confirm in DevTools whether the feed produced
  // enough cards and whether ad slots got interleaved.
  useEffect(() => {
    if (!import.meta.env.DEV) return
    console.log('[WaitScreen] showAds=', showAds,
      'engineCards=', engineCards.length,
      'displayItems=', displayItems.length,
      'adSlots=', displayItems.filter((it) => it.kind === 'ad').length)
  }, [showAds, engineCards.length, displayItems])

  // ── Horizontal scroll-snap (mirrors HomeFeed's swipe behavior) ───────────
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIdx, setActiveIdx] = useState(0)

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const first = el.firstElementChild as HTMLElement | null
    if (!first) return
    const stride = first.offsetWidth + CARD_GAP
    if (stride <= 0) return
    const idx = Math.round(el.scrollLeft / stride)
    if (idx !== activeIdx) setActiveIdx(idx)
  }

  const advanceToNext = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const first = el.firstElementChild as HTMLElement | null
    if (!first) return
    const stride = first.offsetWidth + CARD_GAP
    const next = Math.min(activeIdx + 1, displayItems.length - 1)
    el.scrollTo({ left: next * stride, behavior: 'smooth' })
    setActiveIdx(next)
  }, [activeIdx, displayItems.length])

  // ── Mouse drag-to-scroll (desktop) — mirrors HomeFeed ───────────────────
  // CSS scroll-snap on a div with hidden scrollbar doesn't drag with the
  // mouse natively. Touch devices use touchAction:'pan-x', desktop uses these.
  const isDown = useRef(false)
  const startX = useRef(0)
  const startScrollLeft = useRef(0)

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return
    isDown.current = true
    startX.current = e.pageX - scrollRef.current.offsetLeft
    startScrollLeft.current = scrollRef.current.scrollLeft
    scrollRef.current.style.cursor = 'grabbing'
  }
  const handleMouseLeave = () => {
    isDown.current = false
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab'
  }
  const handleMouseUp = () => {
    isDown.current = false
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab'
  }
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDown.current || !scrollRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollRef.current.offsetLeft
    const walk = (x - startX.current) * 1.5
    scrollRef.current.scrollLeft = startScrollLeft.current - walk
  }

  // ── Card interaction handler (intercepts CTA when !canExit) ─────────────
  // TipCard, GameCard, MilestoneCard → always capture_memory via onRecord.
  // acknowledge cards use the existing markPinnedRead path (no intent needed).
  const handleRecord = useCallback(
    (opts?: RecordingOpts) => {
      if (!canExit) {
        const kind: IntentKind = 'capture_memory'
        const cardContext = opts?.cardContext
        const contentLibraryId = cardContext?.contentLibraryId
        setIntent({
          kind,
          payload:
            cardContext
              ? { cardContext, contentLibraryId }
              : contentLibraryId
                ? { contentLibraryId }
                : undefined,
        })
        toast({ description: t('capture.waitScreen.savedIntent') })
        advanceToNext()
        return
      }
      // canExit: user is about to tap Done — no deferred action needed
    },
    [canExit, setIntent, toast, t, advanceToNext],
  )

  // ── Error state ──────────────────────────────────────────────────────────
  if (processorError !== null) {
    return (
      <div className="flex flex-col items-center gap-4 px-4 py-8 w-full">
        <p className="text-sm text-destructive font-medium">
          {processorError || t('capture.waitScreen.error')}
        </p>
        <div className="flex gap-3 w-full max-w-xs">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    )
  }

  // ── Viewport-fill card-track render ──────────────────────────────────────
  // `fixed inset-0` covers the full viewport so the underlying page can never
  // peek through (mirrors ProcessingScreen). Avoids flex-layout brittleness
  // where an `absolute inset-0` child of a `flex-1` host could mis-size on
  // iOS PWAs when BottomNav unmounts and the host re-flows.
  const headerCopy = processingMode === 'transcript_only'
    ? t('capture.wait.headerTranscribing')
    : t('capture.wait.headerWorking')

  return (
    <div className="fixed inset-0 z-[60] bg-background overflow-hidden flex flex-col">
      {/* Mori status header — persistent during the wait, hidden once user can exit
          (or in skip mode where there's nothing to wait on). */}
      {processingMode !== 'skip' && !canExit && (
        <div className="shrink-0 px-5 pt-3 pb-2 flex items-start gap-3">
          <img
            src="/mori-icon-header.webp"
            alt=""
            aria-hidden
            className="h-9 w-9 shrink-0 mt-0.5"
          />
          <p className="text-sm text-foreground/85 leading-snug">
            {headerCopy}
            {showAds && !minWaitElapsed && (
              <span className="ml-2 inline-block rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground tabular-nums">
                {countdown}s
              </span>
            )}
          </p>
        </div>
      )}

      {/* Horizontal scroll-snap track */}
      <div
        ref={scrollRef}
        className="flex-1 flex scrollbar-hide px-5 pt-2"
        style={{
          gap: CARD_GAP,
          overflowX: 'scroll',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          cursor: 'grab',
          touchAction: 'pan-x',
          userSelect: 'none',
        }}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {displayItems.map((item) => (
          <div
            key={item.key}
            className="flex flex-col items-center justify-center"
            style={{
              width: '100%',
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            {item.kind === 'ad' ? (
              <BannerAdSlot className="w-full" />
            ) : (
              <>
                {/* Saved chip — shown when this card's intent is active */}
                {renderSavedChip(item.card, isCurrent, t)}

                {renderWaitCard(
                  item.card,
                  memories,
                  handleRecord,
                  markPinnedRead,
                  dismissPinnedInstance,
                  trackCardInteraction,
                  dismissCard,
                  canExit,
                  setIntent,
                  toast,
                  t,
                  advanceToNext,
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Sticky progress indicator / Done button — sits inside the host
          container, above the BottomNav (which lives outside the host). */}
      <div className="shrink-0 px-4 pt-3 pb-3 bg-gradient-to-t from-background via-background/90 to-transparent pointer-events-none">
        {canExit ? (
          <button
            type="button"
            onClick={() => onComplete(memoryId!)}
            className="w-full h-12 rounded-2xl bg-primary text-primary-foreground text-sm font-semibold transition-transform active:scale-95 pointer-events-auto"
          >
            {processingMode === 'transcript_only'
              ? t('capture.wait.viewTranscript')
              : processingMode === 'full'
                ? t('capture.wait.viewStory')
                : t('capture.waitScreen.done')}
          </button>
        ) : (
          <div className="flex items-center justify-center h-12">
            {processingMode !== 'skip' ? (
              <span
                className="inline-block h-3 w-3 rounded-full bg-muted-foreground/40 animate-pulse"
                aria-hidden
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getContentId(card: FeedCard): string | undefined {
  if (card.type === 'game' || card.type === 'tip' || card.type === 'milestone') return card.data.id
  if (card.type === 'mori_message') return card.data.contentLibraryId
  return undefined
}

function renderSavedChip(
  card: FeedCard,
  isCurrent: (id: string) => boolean,
  t: (key: string) => string,
) {
  const cid = getContentId(card)
  if (!cid || !isCurrent(cid)) return null
  return (
    <div
      className="absolute top-3 left-3 z-10 rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-medium text-primary-foreground"
      style={{ pointerEvents: 'none' }}
    >
      {t('capture.waitScreen.saved')}
    </div>
  )
}

function renderWaitCard(
  card: FeedCard,
  memories: MemoryWithChildren[],
  onRecord: (opts?: RecordingOpts) => void,
  markPinnedRead: (instanceId: string, contentLibraryId: string) => void,
  dismissPinnedInstance: (instanceId: string, contentLibraryId: string) => void,
  trackCardInteraction: (contentId: string, type: 'shown' | 'yes_tapped' | 'dismissed' | 'swiped_past') => void,
  dismissCard: (contentId: string, childId?: string | null) => void,
  canExit: boolean,
  setIntent: (intent: { kind: IntentKind; payload?: { contentLibraryId?: string } }) => void,
  toast: (opts: { description: string }) => void,
  t: (key: string) => string,
  advanceToNext: () => void,
) {
  // Wrap markPinnedRead for mori_message cards: when !canExit, intercept
  // non-acknowledge CTA actions to save deferred intent before the card
  // handles its own navigation internally.
  const wrappedMarkPinnedRead = (instanceId: string, contentLibraryId: string) => {
    if (!canExit && card.type === 'mori_message') {
      const ctaAction = card.data.ctaAction
      const kind = CTA_TO_INTENT[ctaAction]
      if (kind) {
        setIntent({ kind, payload: { contentLibraryId } })
        toast({ description: t('capture.waitScreen.savedIntent') })
        advanceToNext()
      }
    }
    markPinnedRead(instanceId, contentLibraryId)
  }

  switch (card.type) {
    case 'streak':
      return <StreakCard memories={memories} />
    case 'memory':
      return <FeedMemoryCard memory={card.data} />
    case 'game':
      return <GameCard game={card.data} onRecord={onRecord} />
    case 'tip':
      return <TipCard tip={card.data} onRecord={onRecord} />
    case 'spark':
      return <SparkCard spark={card.data} onRecord={onRecord} />
    case 'milestone':
      return <MilestoneCard milestone={card.data} onRecord={onRecord} />
    case 'collection':
      return <CollectionCard collection={card.data} onRecord={onRecord} />
    case 'mori_message':
      return (
        <MoriMessageCard
          card={card.data}
          onRecord={onRecord}
          markPinnedRead={wrappedMarkPinnedRead}
          dismissPinnedInstance={dismissPinnedInstance}
        />
      )
  }
}
